import { useState } from "react";
import { Outlet } from "react-router";
import { ButterLogo } from "@butter/ui/primitives";
import { MainHeader } from "~/ui/layout/MainHeader";
import { MobileMenu } from "~/ui/navigation/MobileMenu";
import { SignInModal } from "~/ui/layout/SignInModal";
import { useHashModal } from "~/hooks/useHashModal";
import { useAuth, AuthWatcher } from "~/services/auth";
import { useClubQuery } from "~/services/internalApi/club/hooks";
import { ClubGate, useTenantSessionGuard } from "~/services/internalApi/club";
import { useLessonsOffered, useUserLessons } from "~/services/internalApi/lessons";
import { stripPlaceholderName } from "@butter/library/shared";

/**
 * MainLayout - Modern, accessible layout with mobile-first design
 *
 * Features:
 * - Semantic HTML (header, nav, main, footer)
 * - Mobile-first responsive design
 * - Accessible navigation (ARIA labels, keyboard support)
 * - Mobile slide-out menu
 * - Hash-based modal navigation (Android back button support)
 * - Auth state management
 */
export default function MainLayout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const signInModal = useHashModal("dialog-sign-in");
  const { user, isAuthenticated, logout } = useAuth();
  const { data: club } = useClubQuery();
  // "Plans" only for a holder at this club; hidden while loading/unknown.
  const { userLessons } = useUserLessons({ enabled: isAuthenticated });
  // Each lessons link shows only when the club has what it leads to.
  const { hasPackages, hasInstructors } = useLessonsOffered();
  const showPlans = isAuthenticated && userLessons.length > 0;

  // Log out + drop to guest when an authenticated session lands on a different
  // tenant than it belongs to (web tenancy is a query param, so sessions are
  // shared across tenants on one origin). Same-tenant wrong-club access is
  // handled per-CTA via useClubAccess, not here.
  useTenantSessionGuard();

  // Handle successful sign in
  const handleSignInSuccess = () => {
    console.log("[MainLayout] Sign in successful");
    // Use forceClose to remove hash without going back in history
    signInModal.forceClose();
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      console.log("[MainLayout] Logged out successfully");
    } catch (error) {
      console.error("[MainLayout] Logout failed:", error);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      {/* Auth Watcher - Monitors token expiry and refreshes automatically */}
      <AuthWatcher />

      {/* Header */}
      {/* No tenantHandle: TenantBrand reads it from TenantProvider. The prop stays
          on MainHeader as the presentational override its stories drive. */}
      <MainHeader
        clubName={club?.name}
        isAuthenticated={isAuthenticated}
        showBookLesson={hasInstructors}
        showLessonPackages={hasPackages}
        showPlans={showPlans}
        // Placeholder halves (`[unknown]`, imported members with no name on file) render as nothing.
        userFirstName={stripPlaceholderName(user?.firstName)}
        userLastName={stripPlaceholderName(user?.lastName)}
        isMobileMenuOpen={isMobileMenuOpen}
        onToggleMobileMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        onSignIn={signInModal.open}
        onLogout={handleLogout}
      />

      {/* Mobile Menu — the tenant row satisfies TenantAppConfig structurally
          (iosAppId/androidPackageName are string|null, handle is notNull).

          Two nullability notes:
          - This sits outside ClubGate (which wraps only <Outlet/>), so on a
            club-less route, or on a cold first paint before useClubQuery
            resolves, tenant is undefined and app links fall back to the generic
            Butter app. Transient and self-correcting — the query resolving
            re-renders this — and ClubDataPrefetcher usually warms the cache.
          - `?? undefined` is not redundant: getClubById leftJoins tenants and
            can genuinely return `tenant: null`, which ClubWithTenant's type
            over-claims as non-null. This normalizes that to undefined. */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        tenantConfig={club?.tenant ?? undefined}
        isAuthenticated={isAuthenticated}
        showBookLesson={hasInstructors}
        showLessonPackages={hasPackages}
        showPlans={showPlans}
        onLogout={handleLogout}
      />

      {/* Sign In Modal */}
      <SignInModal
        isOpen={signInModal.isOpen}
        onClose={signInModal.close}
        // Dismissing to send the member to account setup must NOT use the history-based
        // close — that pops the browser out of the app instead of closing the dialog.
        onDismiss={signInModal.forceClose}
        onSuccess={handleSignInSuccess}
      />

      {/* Main content area - Centralized layout constraints for all pages */}
      <main
        // flex-1: grows to push the footer to the viewport bottom on short pages.
        className="mx-auto w-full md:min-w-2xl max-w-6xl flex-1 px-4 py-8 sm:px-6 lg:px-8"
        role="main"
      >
        {/* Gate every club-scoped route: skeleton while loading, error only on
            genuine failure, content once club data is ready. */}
        <ClubGate>
          <Outlet />
        </ClubGate>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white" role="contentinfo">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            {/* Footer links */}
            <nav
              className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs font-medium"
              aria-label="Footer navigation"
            >
              <a
                href="https://buttersoftware.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-fg-quaternary transition-colors hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 rounded-sm"
              >
                Terms of service
              </a>
              <a
                href="https://buttersoftware.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-fg-quaternary transition-colors hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 rounded-sm"
              >
                Privacy Policy
              </a>
            </nav>

            {/* Powered by badge */}
            <div className="flex items-center gap-2 text-xs font-medium text-fg-quaternary">
              <a
                href="https://buttersoftware.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 rounded-sm"
                aria-label="Powered by Butter"
              >
                <span>
                  Powered by&nbsp;<span className="sr-only">Butter</span>
                </span>
                <ButterLogo
                  variant="wordmark"
                  className="h-3 w-auto text-gray-900"
                  ariaHidden={true}
                />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
